import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { visit, SKIP } from 'unist-util-visit';

// Componenti renderer definiti A LIVELLO DI MODULO (non in render) per evitare
// re-mount ad ogni ciclo. Mapping selettivo solo dei tag che ci interessano:
// se ReactMarkdown emette un tag non mappato, ricade sul tag HTML nativo.
const MD_COMPONENTS = {
  p: (p) => <p className="my-1.5" {...p} />,
  strong: (p) => <strong className="font-display font-semibold text-ink" {...p} />,
  em: (p) => <em className="italic text-ink/90" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 my-1.5 space-y-0.5" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5 marker:text-mute" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  h1: (p) => <h3 className="font-display text-base mt-3 mb-1.5 text-ink" {...p} />,
  h2: (p) => <h3 className="font-display text-base mt-3 mb-1.5 text-ink" {...p} />,
  h3: (p) => <h4 className="font-mono text-xs uppercase tracking-[0.15em] text-accent mt-3 mb-1" {...p} />,
  blockquote: (p) => (
    <blockquote className="border-l-2 border-accent pl-3 py-0.5 my-2 text-soft italic" {...p} />
  ),
  code: ({ inline, ...p }) =>
    inline
      ? <code className="font-mono text-[13px] bg-linesoft px-1 py-0.5 rounded-sm" {...p} />
      : <code className="font-mono text-[13px] block bg-linesoft p-3 rounded-sm overflow-x-auto" {...p} />,
  a: (p) => (
    <a
      className="text-accent underline decoration-[#E5C9C9] hover:decoration-accent"
      target="_blank" rel="noreferrer noopener"
      {...p}
    />
  ),
  table: (p) => (
    <div className="overflow-x-auto my-2">
      <table className="text-sm border-collapse" {...p} />
    </div>
  ),
  th: (p) => (
    <th className="border border-line px-2 py-1 bg-linesoft font-mono text-[11px] uppercase tracking-[0.15em] text-left" {...p} />
  ),
  td: (p) => <td className="border border-line px-2 py-1" {...p} />,
  hr: () => <hr className="border-t border-line my-3" />,
};

const REMARK = [remarkGfm];

// Schema di sanitizzazione: parte dallo schema di default GitHub e abilita gli
// attributi che ci servono (style/data-area-mention sugli <span> generati dal
// nostro rehype-color-aree). Tutto il resto (script, iframe, on*) viene rimosso.
const SANITIZE_SCHEMA = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [
      ...(defaultSchema.attributes?.span || []),
      'style',
      ['dataAreaMention'],
    ],
  },
};

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Plugin rehype: nei text node del documento, evidenzia le occorrenze dei
 * nomi delle aree (case-insensitive, word-boundary) con uno <span>
 * colorato secondo il colore custom dell'area. Salta i text node dentro
 * <a> e <code> per non rovinare link e codice.
 */
function rehypeColorAreeFactory(aree) {
  return function rehypeColorAree() {
    return (tree) => {
      if (!aree || aree.length === 0) return;
      const items = [...aree]
        .filter((a) => a?.label)
        .sort((a, b) => b.label.length - a.label.length); // match più lunghi prima
      if (items.length === 0) return;

      const lookup = new Map(
        items.map((a) => [a.label.toLowerCase(), a.color || a.colore || 'var(--qp-text-soft)']),
      );
      const regex = new RegExp(
        `\\b(${items.map((a) => escapeRegex(a.label)).join('|')})\\b`,
        'gi',
      );

      visit(tree, 'text', (node, index, parent) => {
        if (!parent || typeof index !== 'number') return;
        if (parent.tagName === 'a' || parent.tagName === 'code') return;
        const value = node.value || '';
        regex.lastIndex = 0;
        if (!regex.test(value)) return;
        regex.lastIndex = 0;

        const newChildren = [];
        let last = 0;
        let m;
        while ((m = regex.exec(value)) !== null) {
          if (m.index > last) {
            newChildren.push({ type: 'text', value: value.slice(last, m.index) });
          }
          const color = lookup.get(m[1].toLowerCase()) || 'var(--qp-text-soft)';
          newChildren.push({
            type: 'element',
            tagName: 'span',
            properties: {
              style: `color: ${color}; font-weight: 600;`,
              'data-area-mention': m[1],
            },
            children: [{ type: 'text', value: m[0] }],
          });
          last = m.index + m[0].length;
        }
        if (last < value.length) {
          newChildren.push({ type: 'text', value: value.slice(last) });
        }
        parent.children.splice(index, 1, ...newChildren);
        return [SKIP, index + newChildren.length];
      });
    };
  };
}

/** Nasconde qualsiasi marker `[CHIPS]…[/CHIPS]` (case-insensitive) e anche
 * marker PARZIALMENTE arrivati durante lo streaming (es. `[CHIPS]opz` senza
 * ancora il `[/CHIPS]` di chiusura). Senza questo, l'utente vede il marker
 * raw per qualche centinaio di ms prima dell'evento `done`. */
function stripChipsMarker(text) {
  if (!text) return text;
  // Marker completo (case-insensitive) — può apparire anche in mezzo al testo.
  let out = text.replace(/[*_`]*\s*\[CHIPS\][\s\S]*?\[\/CHIPS\]\s*[*_`]*/gi, '');
  // Marker INCOMPLETO che inizia ma non si è ancora chiuso: nascondi tutto
  // dal punto di apertura in poi (solo se siamo durante lo streaming = no [/CHIPS]).
  out = out.replace(/[*_`]*\s*\[CHIPS\][\s\S]*$/i, '');
  return out;
}

/**
 * Renderizza il messaggio del coach come Markdown con tipografia coerente
 * (font-body, ink colors). Supporta **grassetto**, *corsivo*, liste, codice,
 * tabelle GFM, blockquote, link esterni.
 *
 * Se `aree` è passato, evidenzia le menzioni dei nomi delle aree con il
 * loro colore custom (lette da `/api/aree`).
 */
export default function CoachMarkdown({ content, aree, className = '' }) {
  const safe = stripChipsMarker(content || '').replace(/\\n/g, '\n');
  const rehypePlugins = useMemo(() => {
    // Ordine: prima i colori-aree (aggiunge gli <span>), poi sanitize (li tiene perché in schema).
    const plugins = [];
    if (Array.isArray(aree) && aree.length > 0) {
      plugins.push(rehypeColorAreeFactory(aree));
    }
    plugins.push([rehypeSanitize, SANITIZE_SCHEMA]);
    return plugins;
  }, [aree]);
  return (
    <div
      className={`font-body text-[15px] leading-relaxed text-ink ${className}`}
      data-testid="coach-markdown"
    >
      <ReactMarkdown remarkPlugins={REMARK} rehypePlugins={rehypePlugins} components={MD_COMPONENTS}>
        {safe}
      </ReactMarkdown>
    </div>
  );
}
